<script setup lang="ts">
import type { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import {
  PICTURE_CHOICE_MIN_COLUMNS,
  type PictureChoiceElement,
  type PictureChoiceImageFit,
  type PictureChoiceOption,
} from "@/types/pictureChoice";
import {
  createPictureChoiceOtherOption,
  createPictureChoiceOption,
  createUniquePictureChoiceOptionLabel,
  clampPictureChoiceColumnsForDevice,
  getPictureChoiceMaxColumnsForDevice,
  getPictureChoiceElementKey,
  normalizePictureChoiceElement,
  validatePictureChoiceElement,
} from "@/util/pictureChoice";
import {
  InfoCircleIcon,
  PlusIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIAlert,
  UIButton,
  UICollapse,
  UICollapseItem,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISelect,
  UISwitch,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep, isEqual } from "lodash";
import { computed, onBeforeUnmount, ref, type PropType, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import PictureChoiceMultipleSelectionSetting from "./PictureChoiceMultipleSelectionSetting.vue";
import PictureChoiceOptionRow from "./PictureChoiceOptionRow.vue";

const props = defineProps({
  element: {
    type: Object as PropType<PictureChoiceElement>,
    required: true,
  },
  mediaCenterSubCategory: {
    type: String as PropType<MediaCenterSubCategory>,
    required: true,
  },
});

const emit = defineEmits<{
  (event: "update:element", element: PictureChoiceElement): void;
}>();

const store = useAppStore();
const { t } = useI18n();

const maximumColumns = computed(() =>
  getPictureChoiceMaxColumnsForDevice(store.deviceMode)
);
const displayedColumnsNumber = computed(() =>
  clampPictureChoiceColumnsForDevice(
    props.element.columnsNumber,
    store.deviceMode
  )
);
const columnsLimitTooltip = computed(() =>
  store.deviceMode === "mobile"
    ? t("fields.choiceElements.pictureChoice.mobileColumnsLimit", {
        count: maximumColumns.value,
      })
    : t("fields.choiceElements.pictureChoice.desktopColumnsLimit", {
        count: maximumColumns.value,
      })
);

const targetedPictureChoiceElement = computed(() => {
  const target = store.pictureChoiceValidationTarget;
  if (!target) return undefined;

  const elements =
    target.slideIndex === undefined
      ? store.elements
      : store.slides[target.slideIndex]?.slideData ?? [];

  if (target.elementKey) {
    return elements.find(
      (element) => getPictureChoiceElementKey(element) === target.elementKey
    );
  }

  return elements[target.elementIndex];
});
const isValidationTarget = computed(
  () => targetedPictureChoiceElement.value === props.element
);
const incompleteOptionErrors = computed(() =>
  validatePictureChoiceElement(props.element).errors.filter(
    ({ code }) =>
      code === "MISSING_OPTION_IMAGE" || code === "MISSING_OPTION_LABEL"
  )
);
const saveValidationWarning = computed(() => {
  if (!isValidationTarget.value) return "";

  return incompleteOptionErrors.value.some(
    ({ code }) => code === "MISSING_OPTION_LABEL"
  )
    ? t("fields.choiceElements.pictureChoice.labelRequiredBeforeSave")
    : incompleteOptionErrors.value.some(
        ({ code }) => code === "MISSING_OPTION_IMAGE"
      )
    ? t("fields.choiceElements.pictureChoice.imageRequiredBeforeSave")
    : "";
});

watch(
  [isValidationTarget, incompleteOptionErrors],
  ([isTarget, errors]) => {
    if (isTarget && errors.length === 0) {
      store.pictureChoiceValidationTarget = undefined;
    }
  },
  { immediate: true }
);

const draggableOptions = ref(cloneDeep(props.element.picklistOptionsImage));
let hasUncommittedOptionLabelChanges = false;

watch(
  () => props.element.picklistOptionsImage,
  (options) => {
    if (isEqual(options, draggableOptions.value)) {
      return;
    }
    draggableOptions.value = cloneDeep(options);
  }
);

const imageFitOptions = computed(() => [
  {
    label: t("fields.choiceElements.pictureChoice.cover"),
    value: "cover",
  },
  {
    label: t("fields.choiceElements.pictureChoice.contain"),
    value: "contain",
  },
  {
    label: t("fields.choiceElements.pictureChoice.fill"),
    value: "fill",
  },
]);

const updateElement = (
  mutate: (element: PictureChoiceElement) => void,
  addToHistory = true
) => {
  if (addToHistory) hasUncommittedOptionLabelChanges = false;

  const nextElement = cloneDeep(props.element);
  mutate(nextElement);
  const normalized = normalizePictureChoiceElement(
    nextElement
  ) as PictureChoiceElement;
  emit("update:element", normalized);
  store.anyUnsavedChanges = true;
  if (addToHistory) {
    store.addToHistory();
  }
};

const updateSimpleProperty = (
  property: keyof PictureChoiceElement,
  value: unknown
) => {
  updateElement((element) => {
    element[property] = value as never;
  });
};

const updateColumnsNumber = (value: unknown) => {
  updateSimpleProperty(
    "columnsNumber",
    clampPictureChoiceColumnsForDevice(value, store.deviceMode)
  );
};

const updateOption = (
  index: number,
  option: PictureChoiceOption,
  addToHistory = true
) => {
  draggableOptions.value[index] = option;
  if (addToHistory) hasUncommittedOptionLabelChanges = false;

  const nextElement = {
    ...props.element,
    picklistOptionsImage: [...props.element.picklistOptionsImage],
  };
  nextElement.picklistOptionsImage[index] = option;
  emit("update:element", nextElement);
  store.anyUnsavedChanges = true;
  if (addToHistory) store.addToHistory();
  if (!addToHistory) hasUncommittedOptionLabelChanges = true;
};

const commitOptionLabelHistory = () => {
  if (!hasUncommittedOptionLabelChanges) return;

  hasUncommittedOptionLabelChanges = false;
  store.addToHistory();
};

onBeforeUnmount(commitOptionLabelHistory);

const updateOptionOrder = () => {
  updateElement((element) => {
    element.picklistOptionsImage = cloneDeep(draggableOptions.value);
  });
};

const addOption = () => {
  updateElement((element) => {
    const label = createUniquePictureChoiceOptionLabel(
      element.picklistOptionsImage
    );
    element.picklistOptionsImage.push(createPictureChoiceOption(label));
  });
};

const removeOption = (index: number) => {
  updateElement((element) => {
    if (element.picklistOptionsImage.length > 1) {
      element.picklistOptionsImage.splice(index, 1);
    }
  });
};

const updateOtherOption = (enabled: boolean) => {
  updateElement((element) => {
    element.allowCustomOption = enabled;
    if (enabled && !element.pictureChoiceSettings.otherOption) {
      element.pictureChoiceSettings.otherOption =
        createPictureChoiceOtherOption();
    }
  });
};

const updateImageFit = (imageFit: PictureChoiceImageFit) => {
  updateElement((element) => {
    element.pictureChoiceSettings.imageFit = imageFit;
  });
};

const updateShowOptionLabel = (showOptionLabel: boolean) => {
  updateElement((element) => {
    element.pictureChoiceSettings.showOptionLabel = showOptionLabel;
  });
};
</script>

<template>
  <div class="picture-choice-options">
    <UICollapse
      id="picture-choice-options-settings"
      arrow-placement="left"
      :default-expanded-names="['Options', 'Option settings']"
    >
      <UICollapseItem
        name="Options"
        :title="t('fields.choiceElements.pictureChoice.options')"
      >
        <div>
          <div
            data-testid="picture-choice-show-option-label"
            class="mb-3 flex items-center justify-between gap-4"
          >
            <div class="text-sm font-medium text-gray-700">
              {{ t("fields.choiceElements.pictureChoice.showOptionLabel") }}
            </div>
            <UISwitch
              size="small"
              :value="element.pictureChoiceSettings.showOptionLabel ?? true"
              @update:value="updateShowOptionLabel(Boolean($event))"
            />
          </div>

          <PictureChoiceMultipleSelectionSetting
            :element="element"
            @update:element="emit('update:element', $event)"
          />

          <VueDraggableNext
            :list="draggableOptions"
            handle=".picture-choice-drag-handle"
            class="divide-y divide-gray-200"
            @change="updateOptionOrder"
          >
            <PictureChoiceOptionRow
              v-for="(option, index) in draggableOptions"
              :key="`${option.createdAt ?? option.src}-${index}`"
              :option="option"
              :index="index"
              :media-center-sub-category="mediaCenterSubCategory"
              :removable="element.picklistOptionsImage.length > 1"
              @update:option="
                (option, addToHistory) =>
                  updateOption(index, option, addToHistory)
              "
              @commit:label="commitOptionLabelHistory"
              @remove="removeOption(index)"
            />
          </VueDraggableNext>

          <UIButton
            id="picture-choice-add-option"
            text
            class="mt-3"
            @click="addOption"
          >
            <PlusIcon class="mr-1.5 h-5 w-5" />
            {{ t("fields.choiceElements.pictureChoice.addOption") }}
          </UIButton>

          <div
            v-if="saveValidationWarning"
            data-testid="picture-choice-save-warning"
            class="pt-3"
          >
            <UIAlert
              id="picture-choice-save-warning"
              type="warning"
            >
              <template #content>
                {{ saveValidationWarning }}
              </template>
            </UIAlert>
          </div>
        </div>
      </UICollapseItem>

      <UICollapseItem
        name="Option settings"
        :title="t('fields.choiceElements.pictureChoice.optionSettings')"
      >
        <div class="space-y-5">
          <div
            v-if="element.dataType !== 'CHECKBOX'"
            class="flex items-start justify-between gap-4"
          >
            <div>
              <div class="text-sm font-medium text-gray-700">
                {{ t("fields.choiceElements.pictureChoice.otherOption") }}
              </div>
            </div>
            <UISwitch
              size="small"
              :value="element.allowCustomOption"
              @update:value="updateOtherOption"
            />
          </div>

          <div class="space-y-1.5">
            <label
              class="block text-sm font-medium text-gray-700"
              for="picture-choice-image-fit"
            >
              {{ t("fields.choiceElements.pictureChoice.imageFit") }}
            </label>
            <UISelect
              id="picture-choice-image-fit"
              :value="element.pictureChoiceSettings.imageFit"
              :options="imageFitOptions"
              @update:value="updateImageFit"
            />
          </div>

          <div class="flex items-center justify-between gap-4">
            <label
              for="picture-choice-spread-columns"
              class="text-sm font-medium text-gray-700"
            >
              {{ t("fields.choiceElements.pictureChoice.spreadToColumns") }}
            </label>
            <UISwitch
              id="picture-choice-spread-columns"
              size="small"
              :value="element.spreadColumns"
              @update:value="
                updateSimpleProperty('spreadColumns', Boolean($event))
              "
            />
          </div>

          <div v-if="element.spreadColumns" class="space-y-1.5">
            <label
              data-testid="picture-choice-columns-label"
              for="picture-choice-columns"
              class="flex items-center text-sm font-medium text-gray-700"
            >
              <span>
                {{ t("fields.choiceElements.pictureChoice.numberOfColumns") }}
              </span>
              <UITooltip trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="ml-1 h-4 w-4 text-gray-400" />
                </template>
                {{ columnsLimitTooltip }}
              </UITooltip>
            </label>
            <UIInputGroup>
              <UIInputNumber
                id="picture-choice-columns"
                class="w-full"
                :model-value="displayedColumnsNumber"
                :min="PICTURE_CHOICE_MIN_COLUMNS"
                :max="maximumColumns"
                :precision="0"
                :show-button="false"
                size="large"
                @update:model-value="updateColumnsNumber"
              />
              <UIInputGroupLabel>
                {{ t("fields.choiceElements.pictureChoice.columns") }}
              </UIInputGroupLabel>
            </UIInputGroup>
          </div>
        </div>
      </UICollapseItem>
    </UICollapse>
  </div>
</template>
